import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { loadingTrue, loadingFalse } from "../../../Reducer/loaderSlice";
import Handler from "../../../Helpers/Handler";
import "../../../Assets/css/style.css";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const STATUS_DOT = {
  SCHEDULED: "#2563eb",
  COMPLETED: "#16a34a",
  CANCELLED: "#dc2626",
};

const MeetingCalendar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-based
  const [calendar, setCalendar] = useState({});
  const [selected, setSelected] = useState(null); // "YYYY-MM-DD"

  const fetchCalendar = async () => {
    dispatch(loadingTrue());
    const res = await Handler({
      method: "GET",
      url: `/meetings/calendar.php?month=${month}&year=${year}`,
    });
    dispatch(loadingFalse());
    if (res.success) {
      setCalendar(res.data?.calendar || {});
    } else {
      toast.error(res.message || "Failed to load calendar");
    }
  };

  useEffect(() => { fetchCalendar(); }, [month, year]);

  /* Build grid of days for current month */
  const buildGrid = () => {
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    return cells;
  };

  const pad = (n) => String(n).padStart(2, "0");
  const toKey = (d) => `${year}-${pad(month)}-${pad(d)}`;

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
    setSelected(null);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
    setSelected(null);
  };

  const cells = buildGrid();
  const todayKey = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

  const selectedMeetings = selected ? (calendar[selected] || []) : [];

  const fmtTime = (t) => {
    const [h, m] = t.split(":");
    const hr = parseInt(h);
    return `${hr % 12 || 12}:${m} ${hr < 12 ? "AM" : "PM"}`;
  };

  return (
    <MainLayout>
      <FeedLayout>
        <div className="mt-page">

          {/* Header */}
          <div className="mt-header">
            <div>
              <h1 className="mt-title">Calendar</h1>
              <p className="mt-subtitle">Monthly view of all scheduled meetings</p>
            </div>
            <div className="mt-header-actions">
              <button className="mt-btn-outline" onClick={() => navigate("/user/meetings")}>
                <i className="bx bx-list-ul" /> List View
              </button>
              <button className="mt-btn-primary" onClick={() => navigate("/user/create-meeting")}>
                <i className="bx bx-plus" /> New Meeting
              </button>
            </div>
          </div>

          {/* Calendar card */}
          <div className="mt-cal-wrap">
            {/* Month nav */}
            <div className="mt-cal-nav">
              <button className="mt-cal-nav-btn" onClick={prevMonth}>
                <i className="bx bx-chevron-left" />
              </button>
              <h2 className="mt-cal-month">{MONTHS[month - 1]} {year}</h2>
              <button className="mt-cal-nav-btn" onClick={nextMonth}>
                <i className="bx bx-chevron-right" />
              </button>
            </div>

            {/* Day labels */}
            <div className="mt-cal-grid mt-cal-header-row">
              {DAYS.map((d) => (
                <div key={d} className="mt-cal-day-label">{d}</div>
              ))}
            </div>

            {/* Date cells */}
            <div className="mt-cal-grid">
              {cells.map((day, idx) => {
                if (!day) return <div key={`e-${idx}`} className="mt-cal-cell mt-cal-empty" />;
                const key = toKey(day);
                const meetings = calendar[key] || [];
                const isToday = key === todayKey;
                const isSelected = key === selected;

                return (
                  <div
                    key={key}
                    className={`mt-cal-cell ${isToday ? "mt-cal-today" : ""} ${isSelected ? "mt-cal-selected" : ""} ${meetings.length ? "mt-cal-has-meetings" : ""}`}
                    onClick={() => setSelected(isSelected ? null : key)}
                  >
                    <span className="mt-cal-num">{day}</span>
                    {meetings.length > 0 && (
                      <div className="mt-cal-dots">
                        {meetings.slice(0, 3).map((m, i) => (
                          <span
                            key={i}
                            className="mt-cal-dot"
                            style={{ background: STATUS_DOT[m.status] || "#2563eb" }}
                          />
                        ))}
                        {meetings.length > 3 && <span className="mt-cal-more">+{meetings.length - 3}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="mt-cal-legend">
              {Object.entries(STATUS_DOT).map(([status, color]) => (
                <span key={status} className="mt-legend-item">
                  <span className="mt-legend-dot" style={{ background: color }} />
                  {status}
                </span>
              ))}
            </div>
          </div>

          {/* Selected day meetings */}
          {selected && (
            <div className="mt-cal-detail">
              <div className="mt-cal-detail-header">
                <h3 className="mt-cal-detail-title">
                  <i className="bx bx-calendar-check" />
                  {new Date(selected + "T00:00:00").toLocaleDateString("en-IN", {
                    weekday: "long", day: "numeric", month: "long", year: "numeric",
                  })}
                </h3>
                <span className="mt-cal-detail-count">
                  {selectedMeetings.length} meeting{selectedMeetings.length !== 1 ? "s" : ""}
                </span>
              </div>

              {selectedMeetings.length === 0 ? (
                <div className="mt-cal-no-meetings">
                  <i className="bx bx-calendar-x" /> No meetings on this day
                </div>
              ) : (
                <div className="mt-cal-meeting-list">
                  {selectedMeetings.map((m) => (
                    <div key={m.meeting_id} className="mt-cal-meeting-item">
                      <div
                        className="mt-cal-meeting-time-bar"
                        style={{ background: STATUS_DOT[m.status] || "#2563eb" }}
                      />
                      <div className="mt-cal-meeting-info">
                        <div className="mt-cal-meeting-time">{fmtTime(m.time)}</div>
                        <div className="mt-cal-meeting-title">{m.title}</div>
                        <span className={`mt-status-badge ${m.status === "SCHEDULED" ? "ms-scheduled" :
                            m.status === "COMPLETED" ? "ms-completed" : "ms-cancelled"
                          }`} style={{ fontSize: 11, padding: "2px 8px" }}>
                          {m.status}
                        </span>
                      </div>
                      {m.status === "SCHEDULED" && (
                        <button
                          className="mt-act-btn mt-act-edit"
                          style={{ marginLeft: "auto", flexShrink: 0 }}
                          onClick={() => navigate(`/user/edit-meeting/${m.meeting_id}`)}
                        >
                          <i className="bx bx-edit" /> Edit
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </FeedLayout>
    </MainLayout>

  );
};

export default MeetingCalendar;